import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Dialog, Card, makeStyles, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import useTemplateTwilio from 'src/hooks/useTemplateTwilio';
import useAuth from 'src/hooks/useAuth';
import { useSnackbar } from 'notistack';
import {
    IModalTemplates,
    ICurrentConversation,
    TConversationTemplateFilters,
    TCurrentTemplate,
} from './types';
import { API_TWILIO } from 'src/constants/conversations';
import { DONT_SHOW } from '../../../../views/dpxChat/components/constants/modalTemplates';
import { TemplateList } from './TemplateList';
import { ButtonSendTemplate } from './ButtonSendTemplate';
import { ITemplate } from 'src/interfaces/templates';
import { hasAppointments } from 'src/utils/hasAppointments';
import { CONVERSATION_SECTION } from 'src/constants/templates';
import { TWILIO_TEMPLATE_VERSIONS } from 'src/modules/models/templateTwilio/constants';
import { buildCurrentTemplate } from '../../helpers/buildCurrentTemplate';
import { applyChannelSender, resolveConversationSender } from '../../channels';
import { getAreaData } from 'src/utils/areas/getDataAreas';
import { SideBarCategories } from './SideBarCategories';
import {
    TEMPLATE_CATEGORY_ALL,
    TEMPLATE_SELECT_QUERY,
    TEMPLATE_CONTEXTS_TYPES,
} from '../../constants/templates';
import { ModalTemplateHeader } from './ModalTemplateHeader';
import { ConversationFactory, TemplateSendFactory } from '../../factories';
import { CURRENT_TEMPLATE_DEFAULT } from '../../constants/threads';

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 'calc(100vw - 32px)',
        maxWidth: 1100,
        maxHeight: 'calc(100vh - 32px)',
        margin: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            width: '100vw',
            maxHeight: '100vh',
            margin: 0,
        },
    },
    card: {
        height: 'min(80vh, calc(100vh - 32px))',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        margin: 0,
        borderRadius: 0,
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 32px)',
            flexDirection: 'column',
        },
        [theme.breakpoints.down('xs')]: {
            height: '100vh',
        },
    },
    contentArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    footer: {
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
    },
}));

export const ModalTemplates = ({
    open,
    setOpen,
    currentTemplate,
    setCurrentTemplate,
    lead,
    serviceClient,
    handleSendTemplate = () => {},
    conversation,
    contextType = TEMPLATE_CONTEXTS_TYPES.AUTO,
    loading = false,
    contextKey,
    ...props
}: IModalTemplates) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { templates: templatesTwilio, getTemplatesV2 } = useTemplateTwilio();
    const getTemplatesV2Ref = useRef(getTemplatesV2);
    const [templates, setTemplates] = useState<TCurrentTemplate[]>([]);
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [currentConversation, setCurrentConversation] =
        useState<ICurrentConversation | null>();
    const [selectedMenuOption, setSelectedMenuOption] = useState<string>(
        TEMPLATE_CATEGORY_ALL
    );

    const resolvedContextType = useMemo(() => {
        if (contextType !== TEMPLATE_CONTEXTS_TYPES.AUTO) return contextType;
        if (serviceClient) return TEMPLATE_CONTEXTS_TYPES.SERVICE_CLIENT;
        if (lead) return TEMPLATE_CONTEXTS_TYPES.LEAD;
        if (conversation) return TEMPLATE_CONTEXTS_TYPES.CONVERSATION;
        return TEMPLATE_CONTEXTS_TYPES.CONVERSATION;
    }, [contextType, serviceClient?._id, lead?._id, conversation?._id]);

    useEffect(() => {
        getTemplatesV2Ref.current = getTemplatesV2;
    }, [getTemplatesV2]);

    const handleGetData = useCallback(
        (currentConversation: ICurrentConversation, category: string) => {
            let filters: TConversationTemplateFilters = {
                areas: currentConversation?.area,
                stores: currentConversation?.store?._id || '',
                type: CONVERSATION_SECTION,
                isActive: true,
            };

            if (category !== TEMPLATE_CATEGORY_ALL) filters.category = category;

            const storeArea = getAreaData({
                area: currentConversation?.area,
                areas: currentConversation?.store?.areas || [],
            });

            if (storeArea?.api === API_TWILIO) {
                filters.templateType =
                    TWILIO_TEMPLATE_VERSIONS.CONTENT_TEMPLATE_BUILDER;
            }

            if (!hasAppointments(currentConversation?.lead?.appointments || []))
                filters.isAppointment = false;

            getTemplatesV2Ref.current({
                filters,
                select: TEMPLATE_SELECT_QUERY,
                type: CONVERSATION_SECTION,
                typeLead: 'lead',
                sort: 'title',
                useCollation: true,
            });
        },
        []
    );

    useEffect(() => {
        if (!open) return;

        if (
            resolvedContextType === TEMPLATE_CONTEXTS_TYPES.SERVICE_CLIENT &&
            serviceClient
        ) {
            setCurrentConversation(
                ConversationFactory.createFromLead(serviceClient)
            );
        } else if (
            resolvedContextType === TEMPLATE_CONTEXTS_TYPES.LEAD &&
            lead
        ) {
            setCurrentConversation(ConversationFactory.createFromLead(lead));
        } else if (
            resolvedContextType === TEMPLATE_CONTEXTS_TYPES.CONVERSATION &&
            conversation
        ) {
            setCurrentConversation(
                ConversationFactory.createFromConversation(conversation)
            );
        } else if (conversation?._id) {
            setCurrentConversation({
                ...conversation,
                isContentTemplateBuilder: true,
            });
        } else {
            setCurrentConversation(null);
        }

        setCurrentTemplate(CURRENT_TEMPLATE_DEFAULT);

        //eslint-disable-next-line
    }, [
        open,
        resolvedContextType,
        conversation?._id,
        lead?._id,
        serviceClient?._id,
        contextKey,
    ]);

    useEffect(() => {
        if (currentConversation?._id)
            handleGetData(currentConversation, selectedMenuOption);
    }, [currentConversation?._id, selectedMenuOption, handleGetData]);

    useEffect(() => {
        if (templatesTwilio && currentConversation?.store?._id) {
            const filteredTemplates: TCurrentTemplate[] = templatesTwilio
                .map((template: ITemplate) =>
                    buildCurrentTemplate(template, currentConversation, user)
                )
                .filter(
                    (currentTemplate: TCurrentTemplate) =>
                        !currentTemplate.description.includes(DONT_SHOW)
                );

            setTemplates(filteredTemplates);
        }
        //eslint-disable-next-line
    }, [templatesTwilio, currentConversation]);

    const handleSubmit = async (currentTemplate: TCurrentTemplate) => {
        if (!currentConversation) return;

        const context = resolvedContextType;
        let data;

        if (
            context === TEMPLATE_CONTEXTS_TYPES.LEAD ||
            context === TEMPLATE_CONTEXTS_TYPES.SERVICE_CLIENT
        ) {
            data = TemplateSendFactory.createForLead(
                currentTemplate,
                currentConversation,
                user
            );
        } else {
            data = TemplateSendFactory.createForConversation(
                currentTemplate,
                currentConversation,
                user
            );
        }

        const areaSender = resolveConversationSender(currentConversation);
        data = applyChannelSender(data, {
            provider: areaSender.provider,
            senderPhone:
                currentConversation.senderPhone || areaSender.senderPhone,
        });
        if (context === TEMPLATE_CONTEXTS_TYPES.CONVERSATION) {
            data.conversationId = currentConversation._id;
            data.senderPhone = currentConversation.senderPhone || data.from;
        }
        if (!data.from) {
            return enqueueSnackbar(`${t('TemplatesTwilio.Error')}`, {
                variant: 'error',
            });
        }
        if (
            context === TEMPLATE_CONTEXTS_TYPES.LEAD ||
            context === TEMPLATE_CONTEXTS_TYPES.SERVICE_CLIENT
        )
            handleSendTemplate(data, lead?._id || serviceClient?._id);
        else handleSendTemplate(data);

        setCurrentTemplate(CURRENT_TEMPLATE_DEFAULT);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentTemplate(CURRENT_TEMPLATE_DEFAULT);
    };

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            fullWidth
            maxWidth="lg"
            PaperProps={{ className: classes.paper }}
            {...props}
        >
            <Card className={classes.card} data-testid="modal-templates">
                <SideBarCategories
                    setSelectedMenuOption={setSelectedMenuOption}
                    selectedMenuOption={selectedMenuOption}
                />
                <Box className={classes.contentArea}>
                    <ModalTemplateHeader onClose={handleClose} />
                    <TemplateList
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        templates={templates}
                    />
                    <Box className={classes.footer}>
                        <ButtonSendTemplate
                            currentTemplate={currentTemplate}
                            submit={handleSubmit}
                            onClose={handleClose}
                            disabled={loading}
                        />
                    </Box>
                </Box>
            </Card>
        </Dialog>
    );
};
